import { Heatmap, type HeatmapProps } from '@mantine/charts';
import dayjs from 'dayjs';

import { makeFieldFormatter } from '#core/lib/formatters/number-decimals.ts';

import classes from '../Chart.module.css';
import type { ChartRendererProps } from '../types.ts';

type HeatmapTooltipInput = Parameters<NonNullable<HeatmapProps['getTooltipLabel']>>[0];

/**
 * Renders heatmap chart.
 *
 * Mantine's `Heatmap` is a calendar heatmap with a single value per date — it
 * cannot render multiple series in one chart. The single-series invariant is
 * enforced upstream by `validateDashboardSpec` in the chat plugin's
 * `ShowDashboardTool.execute`, which throws a structured error that the
 * agent reads. The cell-value column is resolved from `config.series[0].name`
 * when available, falling back to `config.yAxis` for the no-series case.
 */
export function HeatmapRenderer({ config, width, height }: ChartRendererProps) {
  const valueKey = config.series?.[0]?.name ?? config.yAxis;

  const heatmapData: Record<string, number> = {};
  for (const row of config.data) {
    const dateValue = row[config.xAxis];
    const numValue = row[valueKey];
    const numericValue = typeof numValue === 'number' ? numValue : 0;
    if (typeof dateValue === 'string') {
      const parsed = dayjs(dateValue);
      if (parsed.isValid()) {
        heatmapData[parsed.format('YYYY-MM-DD')] = numericValue;
      }
    }
  }

  const dates = Object.keys(heatmapData).sort();
  const startDate = dates.at(0) ?? dayjs().subtract(1, 'year').format('YYYY-MM-DD');
  const endDate = dates.at(-1) ?? dayjs().format('YYYY-MM-DD');

  const rectSize = Math.max(8, Math.min(20, Math.floor(Math.min(width, height) / 10)));
  const formatValue = makeFieldFormatter(config.data, valueKey);

  return (
    <Heatmap
      className={classes.heatmap}
      data={heatmapData}
      endDate={endDate}
      getTooltipLabel={({ date, value }: HeatmapTooltipInput) => `${date}: ${formatValue(value ?? 0)}`}
      rectSize={rectSize}
      startDate={startDate}
      withMonthLabels
      withTooltip
      withWeekdayLabels
    />
  );
}
